/* Sold-out wait list block (BlackMirror, 2026-10-20). Loaded by snippets/bm-restock.liquid.

   What it does: when the selected variant of a product form is sold out, or gated by the stock
   box (the snippet decides that, per variant, and hands it over as g), it shows the wait list
   call to action (rendered hidden by the snippet) and posts the visitor's details to the
   BlackMirror wait list endpoint. The server parses the phone number: the page sends what was
   typed, plus the country of the selected dial code.

   Rules this file keeps, because two earlier scripts froze these pages:
   - It never writes to Dawn's submit button, its label, its disabled flag or the hidden id input.
     The theme row is hidden by CSS only, through one class on the closest buy box.
   - There is no MutationObserver. Variant changes come from a change listener (capture) on the
     id input, the Dawn pub/sub variantChange event, and a 1000 ms read-only interval.
   - Every write is idempotent and happens only when the state changed. The only nodes written
     are our own block, the bm-rs-on class on the closest .product__buy-buttons, the
     bm-rs-page-on class on body, and our own button inside the sticky dock.
   Plain ES5, one IIFE, no build step. */
(function () {
  'use strict';
  /* Loaded twice (a second section render, a product swap): scan for new roots instead. */
  if (window.BMRestock) { window.BMRestock.scan(); return; }

  var ENDPOINT = 'https://pipeline.futuremvmt.com/dashboard-api/api/public/preorder-waitlist';
  var MIN_AGE_MS = 1600;
  var POLL_MS = 1000;
  var ON_CLASS = 'bm-rs-on';
  var PAGE_CLASS = 'bm-rs-page-on';
  var STORE_PREFIX = 'bm-restock:v1:';
  var EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
  var PHONE_MIN_DIGITS = 6;
  var PHONE_MAX_CHARS = 40;
  var TIMEOUT_MS = 15000;
  var BELL_14 = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 6.5a4 4 0 0 1 8 0c0 2.6.8 4 1.5 4.8h-11C3.2 10.5 4 9.1 4 6.5Z"/><path d="M6.5 13.5a1.6 1.6 0 0 0 3 0"/></svg>';

  var instances = [];
  var timer = null;
  var subscribed = false;
  var hashHandled = false;
  var userMoved = false;
  var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  /* ---------- small helpers (all writes compare first) ---------- */

  function setHidden(el, hidden) {
    if (el && el.hidden !== hidden) { el.hidden = hidden; }
  }
  function setText(el, text) {
    if (el && el.textContent !== text) { el.textContent = text; }
  }
  function setAttr(el, name, value) {
    if (el && el.getAttribute(name) !== value) { el.setAttribute(name, value); }
  }
  function setClass(el, name, on) {
    if (!el) { return; }
    if (el.classList.contains(name) !== on) {
      if (on) { el.classList.add(name); } else { el.classList.remove(name); }
    }
  }
  function focusEl(el) {
    if (!el) { return; }
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }
  function reduced() {
    return !!(reduceMq && reduceMq.matches);
  }

  /* ---------- storage (every access guarded: the page must work without it) ---------- */

  function storeGet(variantId) {
    try {
      var raw = window.localStorage.getItem(STORE_PREFIX + variantId);
      if (!raw) { return null; }
      var obj = JSON.parse(raw);
      return obj && typeof obj.phone === 'string' && obj.phone ? obj : null;
    } catch (e) {
      return null;
    }
  }
  function storeSet(variantId, phone) {
    try {
      window.localStorage.setItem(STORE_PREFIX + variantId, JSON.stringify({ phone: phone }));
    } catch (e) { /* storage unavailable, the in-memory copy still works */ }
  }

  /* ---------- phone ---------- */

  /* The typed value, trimmed, with runs of whitespace collapsed. The server does the parsing. */
  function typedPhone(raw) {
    return String(raw || '').replace(/\s+/g, ' ').replace(/^ | $/g, '');
  }

  function digitCount(text) {
    var m = String(text).match(/[0-9]/g);
    return m ? m.length : 0;
  }

  function bestDial(digits, dials) {
    var best = '';
    for (var i = 0; i < dials.length; i++) {
      if (digits.indexOf(dials[i]) === 0 && dials[i].length > best.length) { best = dials[i]; }
    }
    return best;
  }

  /* An E.164 number gets grouped under its dial code. Anything else (the typed fallback) shows as is. */
  function formatPhone(e164, dials) {
    if (String(e164).charAt(0) !== '+') { return String(e164); }
    var digits = String(e164).replace(/^\+/, '');
    var dial = bestDial(digits, dials);
    if (!dial) { return e164; }
    var rest = digits.slice(dial.length).replace(/(\d{3})(?=\d)/g, '$1 ');
    return '+' + dial + ' ' + rest;
  }

  /* ---------- one block ---------- */

  function Block(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.S = cfg.strings || {};
    this.byId = {};
    for (var i = 0; i < cfg.variants.length; i++) { this.byId[cfg.variants[i].id] = cfg.variants[i]; }
    this.fresh = {};
    this.joined = {};
    this.applied = { on: null, vid: null };
    this.lastRead = null;
    this.view = 'cta';
    this.shownPhone = null;
    this.busy = false;
    this.renderedAt = Date.now();
    this.sending = null;

    this.buyBox = root.closest('.product__buy-buttons') || root.parentElement;
    this.cta = root.querySelector('[data-bm-rs-cta]');
    this.hint = root.querySelector('[data-bm-rs-hint]');
    this.panel = root.querySelector('[data-bm-rs-panel]');
    this.title = root.querySelector('[data-bm-rs-heading]');
    this.head = root.querySelector('[data-bm-rs-head]');
    this.forLine = root.querySelector('[data-bm-rs-for]');
    this.form = root.querySelector('[data-bm-rs-form-el]');
    this.errEl = root.querySelector('[data-bm-rs-error]');
    this.submit = root.querySelector('[data-bm-rs-submit]');
    this.done = root.querySelector('[data-bm-rs-done]');
    this.doneBody = root.querySelector('[data-bm-rs-done-body]');
    this.doneTitle = root.querySelector('.bm-rs-done-title');
    this.change = root.querySelector('[data-bm-rs-change]');
    this.errId = this.errEl ? this.errEl.id : '';
    this.nameIn = this.form && this.form.elements.name;
    this.emailIn = this.form && this.form.elements.email;
    this.telIn = this.form && this.form.elements.phone;
    this.dialSel = this.form && this.form.elements.dial;
    this.hpIn = this.form && this.form.elements.hp_url;
    this.ok = !!(this.cta && this.panel && this.form && this.nameIn && this.emailIn && this.telIn && this.dialSel && this.done && this.doneBody);

    this.dials = [];
    if (this.dialSel) {
      for (var j = 0; j < this.dialSel.options.length; j++) {
        var dv = this.dialSel.options[j].value;
        if (this.dials.indexOf(dv) === -1) { this.dials.push(dv); }
      }
    }
  }

  /* The id of the variant the visitor has selected, or '' when none resolves. */
  Block.prototype.readVid = function () {
    var form = document.getElementById(this.cfg.form_id);
    var input = form ? form.querySelector('input[name="id"]') : null;
    if (input) { return String(input.value || '').trim(); }
    var m = /[?&]variant=(\d+)/.exec(window.location.search);
    if (m) { return m[1]; }
    return this.cfg.selected_id || '';
  };

  /* True when the variant takes the wait list: Shopify says it is not available (the page's own
     fresher answer wins over the render time one), or the stock box gates it (g, whatever Shopify
     says, so a gated variant whose availability flips while the page is open keeps the wait list). */
  Block.prototype.waitlist = function (vid) {
    var v = this.byId[vid];
    if (!v) { return false; }
    var gated = v.g === true;
    var available = Object.prototype.hasOwnProperty.call(this.fresh, vid) ? this.fresh[vid] : v.available;
    return available === false || gated;
  };

  /* Only the block in the main product drives the page level parts (body class, dock). A sold out
     quick add modal or featured product keeps to its own block. */
  Block.prototype.isMain = function () {
    var root = this.root;
    if (root.closest('quick-add-modal')) { return false; }
    var cfgs = document.querySelectorAll('[data-bms-config]');
    if (cfgs.length) {
      for (var i = 0; i < cfgs.length; i++) {
        var host = cfgs[i].closest('product-info') || cfgs[i].parentElement;
        if (host && host.contains(root)) { return true; }
      }
      return false;
    }
    var main = document.querySelector('product-info[id^="MainProduct"]');
    return main ? main.contains(root) : true;
  };

  Block.prototype.productLine = function (vid) {
    var text = String(this.cfg.title || '').replace(/^Speediance\s+/, '');
    if (this.cfg.multi) {
      var v = this.byId[vid];
      if (v && v.title && v.title !== 'Default Title') { text += ' · ' + v.title; }
    }
    return text;
  };

  Block.prototype.storedPhone = function (vid) {
    var stored = storeGet(vid);
    if (stored) { return stored.phone; }
    return this.joined[vid] || '';
  };

  /* Reads the selected variant and, only when the sold-out state or the variant changed, writes. */
  Block.prototype.update = function () {
    if (!this.ok || !this.root.isConnected) { return; }
    var vid = this.readVid();
    this.lastRead = vid;
    var on = !!vid && this.waitlist(vid);
    var key = on ? vid : null;
    if (this.applied.on === on && this.applied.vid === key) { return; }
    var prev = this.applied;
    this.applied = { on: on, vid: key };

    if (!on) {
      this.view = 'cta';
      setHidden(this.root, true);
      setClass(this.buyBox, ON_CLASS, false);
      this.applyView();
    } else {
      setHidden(this.root, false);
      setClass(this.buyBox, ON_CLASS, true);
      var phone = this.storedPhone(vid);
      if (phone) {
        this.view = 'done';
        this.phoneShown = phone;
      } else if (this.view === 'done') {
        this.view = 'cta';
      }
      setText(this.forLine, this.productLine(vid));
      this.applyView();
    }
    syncPage();
  };

  /* Shows the parts that belong to the current view. Each write compares first. */
  Block.prototype.applyView = function () {
    var v = this.view;
    setHidden(this.cta, v !== 'cta');
    setHidden(this.hint, v !== 'cta');
    setAttr(this.cta, 'aria-expanded', v === 'open' ? 'true' : 'false');
    setHidden(this.panel, v === 'cta');
    setHidden(this.title, v === 'done');
    setHidden(this.forLine, v === 'done');
    setHidden(this.form, v === 'done');
    setHidden(this.done, v !== 'done');
    if (v === 'done') { this.renderDone(this.phoneShown || ''); }
    syncPage();
  };

  Block.prototype.renderDone = function (phone) {
    if (this.shownPhone === phone) { return; }
    this.shownPhone = phone;
    var parts = String(this.S.done_body || '').split('{phone}');
    this.doneBody.textContent = '';
    for (var i = 0; i < parts.length; i++) {
      if (i > 0) {
        var span = document.createElement('span');
        span.className = 'bm-rs-phone';
        span.textContent = formatPhone(phone, this.dials);
        this.doneBody.appendChild(span);
      }
      if (parts[i]) { this.doneBody.appendChild(document.createTextNode(parts[i])); }
    }
  };

  Block.prototype.setView = function (view) {
    if (this.view === view) { return; }
    this.view = view;
    this.applyView();
  };

  /* Scrolls the block to the scroll margin from the top of the window (120px in the css). scrollIntoView
     is not used: the theme sets scroll-padding-top on the page, which it would add to our margin. */
  function scrollToBlock(root, smooth) {
    var margin = parseFloat(window.getComputedStyle(root).scrollMarginTop) || 0;
    var y = Math.max(0, root.getBoundingClientRect().top + (window.pageYOffset || 0) - margin);
    try {
      window.scrollTo({ top: y, left: 0, behavior: smooth ? 'smooth' : 'instant' });
    } catch (e) {
      window.scrollTo(0, y);
    }
  }

  Block.prototype.reveal = function (target) {
    ourScroll();
    scrollToBlock(this.root, !reduced());
    focusEl(target);
  };

  Block.prototype.open = function (scroll) {
    if (!this.applied.on) { return; }
    if (this.view === 'done') {
      if (scroll) { this.reveal(this.doneTitle); }
      return;
    }
    this.setView('open');
    if (scroll) { this.reveal(this.nameIn); } else { focusEl(this.nameIn); }
  };

  Block.prototype.collapse = function () {
    this.setView('cta');
    focusEl(this.cta);
  };

  /* ---------- form ---------- */

  Block.prototype.showError = function (message) {
    if (!message) { setHidden(this.errEl, true); return; }
    setText(this.errEl, message);
    setHidden(this.errEl, false);
  };

  Block.prototype.clearInvalid = function () {
    var controls = [this.nameIn, this.emailIn, this.telIn];
    for (var i = 0; i < controls.length; i++) { this.clearControl(controls[i]); }
  };

  Block.prototype.clearControl = function (control) {
    if (control.hasAttribute('aria-invalid')) { control.removeAttribute('aria-invalid'); }
    if (control.hasAttribute('aria-describedby')) { control.removeAttribute('aria-describedby'); }
  };

  Block.prototype.invalid = function (control, key) {
    setAttr(control, 'aria-invalid', 'true');
    if (this.errId) { setAttr(control, 'aria-describedby', this.errId); }
    focusEl(control);
    this.showError(this.S[key] || '');
  };

  Block.prototype.setBusy = function (busy) {
    this.busy = busy;
    this.submit.disabled = busy;
    if (busy) { this.submit.setAttribute('aria-busy', 'true'); } else { this.submit.removeAttribute('aria-busy'); }
    setText(this.submit, busy ? (this.S.busy || '') : (this.S.submit || ''));
  };

  Block.prototype.onSubmit = function (event) {
    event.preventDefault();
    if (this.busy) { return; }
    var self = this;
    this.showError('');
    this.clearInvalid();

    var name = this.nameIn.value.trim();
    var email = this.emailIn.value.trim();
    if (!name) { this.invalid(this.nameIn, 'err_name'); return; }
    if (!EMAIL_RE.test(email)) { this.invalid(this.emailIn, 'err_email'); return; }
    var phone = typedPhone(this.telIn.value);
    if (digitCount(phone) < PHONE_MIN_DIGITS || phone.length > PHONE_MAX_CHARS) { this.invalid(this.telIn, 'err_phone'); return; }
    var opt = this.dialSel.options[this.dialSel.selectedIndex];
    var phoneCountry = opt ? (opt.getAttribute('data-iso') || '') : '';

    var vid = this.applied.vid || this.readVid();
    var age = Date.now() - this.renderedAt;
    if (age < MIN_AGE_MS) { age = MIN_AGE_MS; }
    var payload = {
      source: 'sold-out-product',
      product: this.cfg.product,
      product_id: this.cfg.product_id,
      variant_id: vid,
      name: name,
      email: email,
      phone: phone,
      phone_country: phoneCountry,
      locale: this.cfg.locale,
      country: this.cfg.country,
      path: this.cfg.path,
      marketing_consent: true,
      hp_url: this.hpIn ? this.hpIn.value : '',
      form_age_ms: age
    };

    if (typeof window.fetch !== 'function') { this.showError(this.S.err_generic || ''); return; }
    var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var timeout = controller ? window.setTimeout(function () { controller.abort(); }, TIMEOUT_MS) : null;
    this.setBusy(true);
    var init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    };
    if (controller) { init.signal = controller.signal; }
    window.fetch(ENDPOINT, init).then(function (response) {
      /* 204 is the honeypot's silent drop: answer it like a success, with no phone back. */
      if (response.status === 204) { return {}; }
      return response.json().catch(function () { return {}; }).then(function (body) {
        if (!response.ok) {
          var failure = new Error('server');
          failure.status = response.status;
          failure.field = body && typeof body.field === 'string' ? body.field : '';
          throw failure;
        }
        return body || {};
      });
    }).then(function (body) {
      if (timeout) { window.clearTimeout(timeout); }
      self.setBusy(false);
      /* Show and keep the number the server saved (E.164); fall back to what was typed. */
      var saved = body && typeof body.phone === 'string' && body.phone ? body.phone : phone;
      self.joined[vid] = saved;
      storeSet(vid, saved);
      /* The visitor may have picked another variant while the request ran: only the variant that
         was submitted (still the shown one) switches to Done. */
      if (self.applied.vid === vid) {
        self.phoneShown = saved;
        self.view = 'done';
        self.applyView();
        focusEl(self.doneTitle);
      }
    }).catch(function (error) {
      if (timeout) { window.clearTimeout(timeout); }
      self.setBusy(false);
      /* Only our own localised lines are ever shown, never server text. */
      if (error && error.status === 400 && error.field === 'phone') { self.invalid(self.telIn, 'err_phone'); return; }
      if (error && error.status === 409) { self.showError(self.S.err_instock || self.S.err_generic || ''); return; }
      self.showError(self.S.err_generic || '');
    });
  };

  /* Reopens the form from the Done view with the fields still filled. */
  Block.prototype.onChange = function () {
    if (!this.telIn.value && this.phoneShown) {
      if (this.phoneShown.charAt(0) !== '+') {
        this.telIn.value = this.phoneShown;
      } else {
        var digits = this.phoneShown.replace(/^\+/, '');
        var dial = bestDial(digits, this.dials);
        var matches = 0;
        var matchedIndex = -1;
        if (dial) {
          for (var i = 0; i < this.dialSel.options.length; i++) {
            if (this.dialSel.options[i].value === dial) { matches++; matchedIndex = i; }
          }
        }
        if (matches === 1) {
          this.dialSel.selectedIndex = matchedIndex;
          this.telIn.value = digits.slice(dial.length);
        } else {
          /* Shared calling codes do not identify a country. Keep the selected ISO and full
             international number so the server can parse it without guessing a new country. */
          this.telIn.value = this.phoneShown;
        }
      }
    }
    this.setView('open');
    focusEl(this.telIn);
  };

  Block.prototype.bind = function () {
    var self = this;
    this.cta.addEventListener('click', function () { self.open(false); });
    if (this.head) { this.head.addEventListener('click', function () { self.collapse(); }); }
    this.form.addEventListener('submit', function (event) { self.onSubmit(event); });
    if (this.change) { this.change.addEventListener('click', function () { self.onChange(); }); }
    var controls = [this.nameIn, this.emailIn, this.telIn];
    for (var i = 0; i < controls.length; i++) {
      controls[i].addEventListener('input', function (event) {
        self.clearControl(event.target);
        if (!self.form.querySelector('[aria-invalid="true"]')) { self.showError(''); }
      });
    }
  };

  /* ---------- page level ---------- */

  /* The block that drives the page level parts: shown, and in the main product. */
  function activeBlock() {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].applied.on && instances[i].isMain()) { return instances[i]; }
    }
    return null;
  }

  /* While every active block is in Done, our own dock button has nothing left to offer. */
  function syncDockHidden() {
    var button = document.querySelector('.bms-dock .bm-rs-dock-cta');
    if (!button) { return; }
    var any = false;
    var allDone = true;
    for (var i = 0; i < instances.length; i++) {
      if (!instances[i].applied.on || !instances[i].isMain()) { continue; }
      any = true;
      if (instances[i].view !== 'done') { allDone = false; }
    }
    setHidden(button, any && allDone);
  }

  function onDockClick() {
    var block = activeBlock();
    if (block) { block.open(true); }
  }

  /* Our own button inside the sticky dock. Never touches .bms-dock-buy. */
  function ensureDock() {
    var block = activeBlock();
    var dock = document.querySelector('.bms-dock');
    if (!block || !dock || dock.querySelector('.bm-rs-dock-cta')) { return; }
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'bm-rs-dock-cta';
    button.innerHTML = BELL_14;
    var label = document.createElement('span');
    label.textContent = block.S.card_cta || '';
    button.appendChild(label);
    button.addEventListener('click', onDockClick);
    var buy = dock.querySelector('.bms-dock-buy');
    if (buy && buy.parentNode) { buy.parentNode.insertBefore(button, buy.nextSibling); } else { dock.appendChild(button); }
  }

  function syncPage() {
    var any = !!activeBlock();
    setClass(document.body, PAGE_CLASS, any);
    if (any) { ensureDock(); }
    syncDockHidden();
  }

  function prune() {
    var kept = [];
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].root.isConnected) { kept.push(instances[i]); }
    }
    if (kept.length !== instances.length) {
      instances = kept;
      syncPage();
    }
  }

  function tick() {
    if (document.hidden) { return; }
    scan();
    prune();
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].readVid() !== instances[i].lastRead) { instances[i].update(); }
    }
    if (document.body.classList.contains(PAGE_CLASS)) { ensureDock(); syncDockHidden(); }
  }

  function onIdChange(event) {
    var t = event.target;
    if (!t || t.tagName !== 'INPUT' || t.name !== 'id' || !t.form) { return; }
    /* A form that holds an input named id answers form.id with that input, so read the attribute. */
    var formId = t.form.getAttribute('id');
    for (var i = 0; i < instances.length; i++) {
      if (formId === instances[i].cfg.form_id) { instances[i].update(); }
    }
  }

  /* Dawn pub/sub. Its payload carries fresher availability than our render-time list. */
  function onVariantChange(message) {
    try {
      var data = message && message.data;
      if (!data || !data.variant) { return; }
      var vid = String(data.variant.id);
      for (var i = 0; i < instances.length; i++) {
        var block = instances[i];
        if (data.sectionId && block.cfg.section_id && String(data.sectionId) !== block.cfg.section_id) { continue; }
        if (block.byId[vid] && typeof data.variant.available === 'boolean') { block.fresh[vid] = data.variant.available; }
        block.update();
      }
    } catch (e) { /* the next tick re-reads the variant */ }
  }

  /* subscribe is a function declaration (a window property). PUB_SUB_EVENTS is a top level const in
     constants.js, so it is a global binding but not a window property: read it with typeof. */
  function trySubscribe() {
    if (subscribed || typeof window.subscribe !== 'function') { return; }
    var name = 'variant-change';
    if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
      name = PUB_SUB_EVENTS.variantChange;
    }
    window.subscribe(name, onVariantChange);
    subscribed = true;
  }

  function wantsHash() {
    return window.location.hash === '#bm-restock';
  }

  var hashBlock = null;

  function openFromHash() {
    var block = activeBlock();
    if (block && wantsHash()) {
      hashBlock = block;
      block.open(true);
      if (document.readyState === 'complete') { window.setTimeout(settleHashTwice, 300); }
    }
  }

  /* The shopper has scrolled when a wheel, touch or scroll key came after our last scroll. Plain scroll
     events are not counted: the images that load above the block shift the layout and raise them too. */
  function ourScroll() { userMoved = false; }
  function noteUser() { userMoved = true; }
  var scrollKeys = { 33: 1, 34: 1, 35: 1, 36: 1, 32: 1, 38: 1, 40: 1 };
  window.addEventListener('wheel', noteUser, { passive: true });
  window.addEventListener('touchmove', noteUser, { passive: true });
  window.addEventListener('keydown', function (event) { if (scrollKeys[event.keyCode]) { noteUser(); } }, { passive: true });

  /* Images above the block can load after the first scroll and push it down. After the page has loaded,
     and once more 300 ms later, put the block back at the top (the scroll margin leaves room for the
     sticky header), but only when the shopper has not scrolled since our last scroll. A read, and a
     write only when the block drifted. */
  function settleHash() {
    if (!hashBlock || !hashBlock.root.isConnected || !hashBlock.applied.on || userMoved) { return; }
    var rect = hashBlock.root.getBoundingClientRect();
    var margin = parseFloat(window.getComputedStyle(hashBlock.root).scrollMarginTop) || 0;
    if (Math.abs(rect.top - margin) > 4) {
      ourScroll();
      scrollToBlock(hashBlock.root, false);
    }
  }

  function settleHashTwice() {
    settleHash();
    window.setTimeout(function () { settleHash(); hashBlock = null; }, 300);
  }

  function scan() {
    var roots = document.querySelectorAll('[data-bm-restock]:not([data-bm-rs-ready])');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      root.setAttribute('data-bm-rs-ready', '1');
      var blob = root.querySelector('script[data-bm-restock-data]');
      var cfg = null;
      try { cfg = JSON.parse(blob.textContent); } catch (e) { cfg = null; }
      if (!cfg || !cfg.variants) { continue; }
      var block = new Block(root, cfg);
      if (!block.ok) { continue; }
      block.bind();
      instances.push(block);
      block.update();
    }
    if (roots.length) {
      trySubscribe();
      if (!timer) { timer = window.setInterval(tick, POLL_MS); }
      if (!hashHandled) { hashHandled = true; openFromHash(); }
    }
  }

  function boot() {
    scan();
  }

  window.BMRestock = { scan: scan };

  document.addEventListener('change', onIdChange, true);
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('product-info:loaded', boot);
  window.addEventListener('hashchange', openFromHash);
  window.addEventListener('load', function () {
    trySubscribe();
    syncPage();
    if (hashBlock) { settleHashTwice(); }
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
